import React, { useEffect, useRef, useState } from "react";

const OfficeCaptureFields = ({
    demoAppInstalled,
    onDemoAppInstalledChange,
    onPhotoSelected,
    onLocationCaptured,
    existingPhotoUrl,
    existingLatitude,
    existingLongitude,
}) => {
    const [previewUrl, setPreviewUrl] = useState(null);
    const [locationState, setLocationState] = useState("idle"); // idle | capturing | success | error
    const [locationError, setLocationError] = useState("");
    const [capturedCoords, setCapturedCoords] = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    const handleFileChange = (e) => {
        const file = e.target.files && e.target.files[0];
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }
        if (file) {
            setPreviewUrl(URL.createObjectURL(file));
        } else {
            setPreviewUrl(null);
        }
        onPhotoSelected(file || null);
    };

    const handleRemovePhoto = () => {
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }
        setPreviewUrl(null);
        onPhotoSelected(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleCaptureLocation = () => {
        if (!navigator.geolocation) {
            setLocationState("error");
            setLocationError("Geolocation is not supported on this device.");
            return;
        }

        setLocationState("capturing");
        setLocationError("");

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords;
                setCapturedCoords({ latitude, longitude });
                setLocationState("success");
                onLocationCaptured(latitude, longitude);
            },
            (error) => {
                setLocationState("error");
                setLocationError(error.message || "Could not get your location.");
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
    };

    const hasExistingPhoto = !!existingPhotoUrl;
    const hasExistingLocation = existingLatitude != null && existingLongitude != null;
    const hasExistingDemoAppInstalled = demoAppInstalled === true;

    return (
        <div className="office-capture-fields">
            {!hasExistingPhoto && (
                <div className="mb-3">
                    <label htmlFor="officePhoto" className="form-label">
                        Office Photo:
                    </label>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="form-control"
                        id="officePhoto"
                        onChange={handleFileChange}
                    />
                    {previewUrl && (
                        <div className="mt-2">
                            <img
                                src={previewUrl}
                                alt="Office preview"
                                style={{ maxWidth: "160px", maxHeight: "160px", display: "block" }}
                            />
                            <button
                                type="button"
                                className="btn btn-link p-0"
                                onClick={handleRemovePhoto}
                            >
                                Remove
                            </button>
                        </div>
                    )}
                </div>
            )}

            {!hasExistingLocation && (
                <div className="mb-3">
                    <label className="form-label d-block">Office Location:</label>
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={handleCaptureLocation}
                        disabled={locationState === "capturing"}
                    >
                        {locationState === "capturing" ? "Capturing…" : "📍 Capture Current Location"}
                    </button>
                    <div className="mt-1">
                        {locationState === "success" && capturedCoords && (
                            <small className="text-success">
                                ✓ Location captured ({capturedCoords.latitude.toFixed(5)}, {capturedCoords.longitude.toFixed(5)})
                            </small>
                        )}
                        {locationState === "error" && (
                            <small className="text-danger">{locationError}</small>
                        )}
                    </div>
                </div>
            )}

            {!hasExistingDemoAppInstalled && (
                <div className="mb-3">
                    <label className="form-label d-block">Demo App Installed:</label>
                    <div className="form-check form-check-inline">
                        <input
                            className="form-check-input"
                            type="radio"
                            name="demoAppInstalled"
                            id="demoAppInstalledYes"
                            checked={demoAppInstalled === true}
                            onChange={() => onDemoAppInstalledChange(true)}
                        />
                        <label className="form-check-label" htmlFor="demoAppInstalledYes">
                            Yes
                        </label>
                    </div>
                    <div className="form-check form-check-inline">
                        <input
                            className="form-check-input"
                            type="radio"
                            name="demoAppInstalled"
                            id="demoAppInstalledNo"
                            checked={demoAppInstalled === false}
                            onChange={() => onDemoAppInstalledChange(false)}
                        />
                        <label className="form-check-label" htmlFor="demoAppInstalledNo">
                            No
                        </label>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OfficeCaptureFields;
